import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

export default function Guestbook() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", location: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setLoading(true);
    api
      .get("/guestbook")
      .then((r) => setEntries(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) {
      toast.error("Please leave a name and a message.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/guestbook", form);
      setEntries((prev) => [data, ...prev]);
      setForm({ name: "", location: "", message: "" });
      toast.success("Thank you for signing the book.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-[1300px] mx-auto px-6 sm:px-10 py-20 fade-up" data-testid="guestbook-page">
      <p className="plaque-label">Guestbook</p>
      <h1 className="font-serif-display font-light text-5xl sm:text-7xl leading-[0.95] mt-4 max-w-3xl">
        Leave a word
        <br />
        on the way out.
      </h1>
      <p className="text-zinc-400 mt-6 max-w-2xl text-lg">
        A page kept open near the door. Write what you felt, where you stood, or what stayed with
        you. Names are visible. Email is not asked.
      </p>

      <div className="grid md:grid-cols-12 gap-16 mt-16">
        {/* Form */}
        <form onSubmit={submit} className="md:col-span-5 space-y-6" data-testid="guestbook-form">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="plaque-label">Name</label>
              <input
                type="text"
                value={form.name}
                onChange={update("name")}
                className="line-input"
                placeholder="Jane Doe"
                data-testid="guestbook-name-input"
                maxLength={60}
                required
              />
            </div>
            <div>
              <label className="plaque-label">From (optional)</label>
              <input
                type="text"
                value={form.location}
                onChange={update("location")}
                className="line-input"
                placeholder="Amsterdam"
                data-testid="guestbook-location-input"
                maxLength={80}
              />
            </div>
          </div>
          <div>
            <label className="plaque-label">Message</label>
            <textarea
              rows={5}
              value={form.message}
              onChange={update("message")}
              className="line-input resize-none"
              placeholder="What stayed with you?"
              data-testid="guestbook-message-input"
              maxLength={600}
              required
            />
            <p className="text-xs text-zinc-600 mt-1">
              {form.message.length}/600
            </p>
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="group inline-flex items-center gap-3 text-xs tracking-[0.3em] uppercase border border-[#D4AF37]/40 hover:border-[#D4AF37] px-6 py-4 text-[#D4AF37] transition-colors disabled:opacity-50"
            data-testid="guestbook-submit-button"
          >
            {submitting ? "Signing…" : "Sign the book"}
            <span className="transition-transform group-hover:translate-x-1">→</span>
          </button>
        </form>

        {/* Entries */}
        <div className="md:col-span-7" data-testid="guestbook-entries">
          <div className="flex items-baseline justify-between mb-8">
            <p className="plaque-label">Signatures</p>
            <span className="text-xs text-zinc-500">
              {entries.length} {entries.length === 1 ? "entry" : "entries"}
            </span>
          </div>

          {loading ? (
            <p className="text-zinc-500" data-testid="guestbook-loading">Loading…</p>
          ) : entries.length === 0 ? (
            <div className="border border-dashed border-white/10 p-12 text-center" data-testid="guestbook-empty">
              <p className="font-serif-display text-2xl italic text-zinc-400">
                The book is empty.
              </p>
              <p className="text-zinc-500 mt-2 text-sm">Be the first to sign.</p>
            </div>
          ) : (
            <ul className="space-y-6">
              <AnimatePresence initial={false}>
                {entries.map((e, idx) => (
                  <motion.li
                    key={e.id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4, delay: idx === 0 ? 0 : 0 }}
                    className="border-l border-[#D4AF37]/30 pl-6 py-2"
                    data-testid={`guestbook-entry-${e.id}`}
                  >
                    <p className="text-zinc-200 leading-relaxed font-serif-display text-lg italic">
                      "{e.message}"
                    </p>
                    <div className="mt-3 flex flex-wrap items-baseline gap-x-3 text-xs">
                      <span className="text-zinc-300 tracking-wide">— {e.name}</span>
                      {e.location && (
                        <span className="text-zinc-500 italic">{e.location}</span>
                      )}
                      <span className="text-zinc-600 ml-auto">{formatDate(e.created_at)}</span>
                    </div>
                  </motion.li>
                ))}
              </AnimatePresence>
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
